import React from 'react';
import { Box, Typography, Chip, Collapse, IconButton } from '@mui/material';
import { FiCpu, FiTool, FiChevronDown, FiChevronUp } from 'react-icons/fi';

interface ThinkingDisplayProps {
  thinking?: string | string[];
  toolCalls?: Array<{ name: string; args: any }>;
  steps?: string[];
  isLive?: boolean; // Indicates if this is live streaming content
}

const ThinkingDisplay: React.FC<ThinkingDisplayProps> = ({ thinking, toolCalls = [] }) => {
  const [expanded, setExpanded] = React.useState(true);
  const thinkingItems: string[] = Array.isArray(thinking)
    ? thinking
    : (thinking ? String(thinking).split(/\n+/).map(s => s.trim()).filter(Boolean) : []);
  
  if (thinkingItems.length === 0 && toolCalls.length === 0) {
    return null;
  }

  return (
    <Box
      className="thinking-display-root"
      sx={{
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
        p: 2,
        mb: 2,
        border: '1px solid #e0e0e0',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <FiCpu size={16} color="#666" />
          <Typography variant="body2" fontWeight={600} color="textSecondary">
            AI Reasoning Process
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded} className="thinking-display-collapse">
        <Box mt={2} className="thinking-display-content">
          {/* Thinking section */}
          {thinkingItems.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                Current Thinking:
              </Typography>
              <Box
                className="thinking-display-list"
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  p: 1.5,
                  color: '#333',
                  borderLeft: '3px solid #2196f3',
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                <ul style={{ margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
                  {thinkingItems.map((item, idx) => (
                    <li key={idx} style={{ margin: '6px 0', lineHeight: 1.4 }}>
                      <span style={{ fontSize: '0.9rem' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>
          )}

          {/* Tool calls section */}
          {toolCalls.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                Tools Being Used:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {toolCalls.map((tool, index) => (
                  <Chip
                    key={index}
                    icon={<FiTool size={14} />}
                    label={tool.name}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Steps section - removed per user request */}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ThinkingDisplay;
